import {Box} from 'src/design/atoms/Box';
import {Group} from 'src/design/atoms/Group';
import {Paper} from 'src/design/atoms/Paper';
import {ScrollArea} from 'src/design/atoms/ScrollArea';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import {Draggable, Droppable} from '@hello-pangea/dnd';
import {IconCalendarPlus, IconSearch} from '@tabler/icons-react';
import React from 'react';
import {ActionIcon} from 'src/design/atoms/ActionIcon';
import {Pill} from 'src/design/atoms/Pill';
import {SectionCard} from 'src/design/atoms/SectionCard';
import {Textbox} from 'src/design/atoms/Textbox';
import {formatUtcShortDate} from 'src/shared/utils/formatters';

export interface DispatchQueueEntry {
  id: number;
  siteName: string;
  inspectionTypeName: string;
  dueAt?: Date;
  isPastDue: boolean;
}

export interface DispatchQueuePanelProps {
  entries: DispatchQueueEntry[];
  search: string;
  onSearchChange: (value: string) => void;
  onSchedule: (id: number) => void;
  isDragActive: boolean;
}

export const QUEUE_DROPPABLE_ID = 'dispatch-queue';

export const DispatchQueuePanel: React.FC<DispatchQueuePanelProps> = ({
  entries,
  search,
  onSearchChange,
  onSchedule,
  isDragActive,
}) => (
  <SectionCard title="Queue">
    <Box p="md">
      <Stack gap="sm">
        <Textbox
          ariaLabel="Search queue"
          placeholder="Search unassigned"
          value={search}
          onChangeCallback={e => onSearchChange(e.target.value)}
          props={{leftSection: <IconSearch size={16} />}}
        />

        {isDragActive && (
          <Text size="xs" c="dimmed" ta="center">
            Drop here to unassign
          </Text>
        )}

        <Droppable droppableId={QUEUE_DROPPABLE_ID}>
          {(provided, snapshot) => (
            <ScrollArea.Autosize mah={560}>
              <Box
                style={{
                  transition: 'background 120ms ease, box-shadow 120ms ease',
                  borderRadius: 8,
                  minHeight: 80,
                  background: snapshot.isDraggingOver
                    ? 'var(--mantine-color-yellow-0)'
                    : undefined,
                  outline: isDragActive
                    ? `2px dashed ${
                        snapshot.isDraggingOver
                          ? 'var(--mantine-color-yellow-6)'
                          : 'var(--mantine-color-gray-4)'
                      }`
                    : undefined,
                  outlineOffset: -2,
                }}>
                <Stack
                  gap="xs"
                  ref={provided.innerRef}
                  {...provided.droppableProps}>
                  {entries.length === 0 ? (
                    isDragActive ? null : (
                      <Text size="sm" c="dimmed" py="md" ta="center">
                        No unassigned inspections — everything is dispatched.
                      </Text>
                    )
                  ) : (
                    entries.map((entry, index) => (
                      <Draggable
                        key={entry.id}
                        draggableId={String(entry.id)}
                        index={index}>
                        {(dragProvided, dragSnapshot) => (
                          <Paper
                            withBorder
                            p="xs"
                            ref={dragProvided.innerRef}
                            {...dragProvided.draggableProps}
                            {...dragProvided.dragHandleProps}
                            style={{
                              ...dragProvided.draggableProps.style,
                              cursor: 'grab',
                              ...(dragSnapshot.isDragging
                                ? {boxShadow: 'var(--mantine-shadow-lg)'}
                                : {}),
                            }}>
                            <Group
                              justify="space-between"
                              wrap="nowrap"
                              gap="xs">
                              <Stack gap={2} style={{minWidth: 0}}>
                                <Text size="sm" fw={600} truncate>
                                  {entry.siteName}
                                </Text>
                                <Text size="xs" c="dimmed" truncate>
                                  {entry.inspectionTypeName}
                                </Text>
                              </Stack>
                              <ActionIcon
                                variant="subtle"
                                color="gray"
                                tooltipText="Schedule inspection"
                                onClick={() => onSchedule(entry.id)}>
                                <IconCalendarPlus size={16} />
                              </ActionIcon>
                            </Group>
                            <Group gap="xs" mt={4}>
                              {entry.isPastDue && (
                                <Pill text="Past due" color="red" />
                              )}
                              <Text size="xs" c="dimmed">
                                Due {formatUtcShortDate(entry.dueAt)}
                              </Text>
                            </Group>
                          </Paper>
                        )}
                      </Draggable>
                    ))
                  )}
                  {provided.placeholder}
                </Stack>
              </Box>
            </ScrollArea.Autosize>
          )}
        </Droppable>
      </Stack>
    </Box>
  </SectionCard>
);
